import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import QRCode from 'react-native-qrcode-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import DeleteIcon16 from '../../../16pxIcons/DeleteIcon16';
import CopyIcon from '../../../24pxIcons/CopyIcon';
import PlusIcon from '../../../16pxIcons/PlusIcon';
import AddQRCodeModal from '../../AddQRCodeModal';
import deleteSolveTag from '../../../../requests/Assets/DeleteSolveTag';
import PressableStyled from '../../../PressableStyled';
import { LINK_COPIED } from '../../../../constants/AlertMessages';

interface AssetTagSectionProps {
  assetId: string,
  showAlert,
  tags: string[],
  addTag,
  removeTag,
  tagIndex: number,
  setTagIndex: Dispatch<SetStateAction<number>>,
}

const AssetTagSection = ({
  assetId, showAlert, tags, addTag, removeTag, tagIndex = 0, setTagIndex,
}: AssetTagSectionProps) => {
  const [qrTabSelected, setQrTabSelected] = useState<boolean>(true);
  const [domain, setDomain] = useState<string>('https://hereworks.tech/');
  const [copied, setCopied] = useState<boolean>(false);
  const [addQRCodeModalShown, setAddQRCodeModalShown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    AsyncStorage.getItem('environment').then((result) => {
      if (result === 'dev') setDomain('https://dev.hereworks.tech/');
      else if (result === 'demo') setDomain('https://demo.hereworks.tech/');
    });
  }, []);

  const link: string = `${`${domain}${tags[tagIndex] ?? assetId}`}?ss=${qrTabSelected ? 'QR' : 'NFC'}`;

  const addTagToAsset = (tag: string) => {
    addTag(tag);
    setTagIndex(tags.length);
  };

  const deleteTag = () => showAlert(
    'Remove tag?',
    'Are you sure you would like to remove this tag from this asset?',
    true,
    () => {
      setLoading(true);
      deleteSolveTag(assetId, tags[tagIndex]).then((res) => {
        if (res) {
          const tagId: string = tags[tagIndex];
          if (tagIndex === tags.length - 1) setTagIndex(tagIndex - 1);
          removeTag(tagId);
          showAlert(
            'Solve tag removed',
            <>
              <Text style={Style.alertBoldText}>{tagId}</Text>
              {' was successfully removed from this asset'}
            </>,
            false,
          );
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    },
    'Remove',
  );

  const copyLink = () => {
    Clipboard.setStringAsync(link);
    setCopied(true);
    const timeout = setTimeout(() => {
      setCopied(false);
      clearTimeout(timeout);
    }, 3000);
  };

  const hideQRCodeModal = () => setAddQRCodeModalShown(false);

  return <View style={styles.container}>
    <View style={styles.tabContainer}>
      <TouchableOpacity
        onPress={() => setQrTabSelected(true)}
        style={styles.tabButton}
        disabled={qrTabSelected}
      >
        <Text style={[styles.tabButtonText, qrTabSelected && styles.tabButtonTextSelected]}>
          {'QR CODE'}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => setQrTabSelected(false)}
        style={styles.tabButton}
        disabled={!qrTabSelected}
      >
        <Text style={[styles.tabButtonText, !qrTabSelected && styles.tabButtonTextSelected]}>
          {'NFC'}
        </Text>
      </TouchableOpacity>
    </View>
    <View style={styles.content}>
      <View style={[styles.data, Style.row]}>
        <View style={[Style.row, styles.image]}>
          {qrTabSelected ? <QRCode
            value={link}
            size={50}
          /> : <FontAwesome name={'wifi'} size={50} />}
        </View>
        <View style={styles.tagId}>
          <View style={Style.row}>
            <Text style={styles.assetId}>{`ID: ${tags[tagIndex] ?? assetId}`}</Text>
            {tags.length > 1 && <TouchableOpacity
              onPress={deleteTag}
              hitSlop={{
                top: 4, bottom: 4, left: 4, right: 4,
              }}
            >
              <DeleteIcon16 color={Colors.light.hereworksRed500} />
            </TouchableOpacity>}
          </View>
          <View style={Style.row}>
            <TouchableOpacity
              hitSlop={{
                top: 4, bottom: 4, left: 4, right: 4,
              }}
              onPress={() => setTagIndex(Math.max(tagIndex - 1, 0))}
              disabled={tagIndex <= 0}
              style={{ opacity: tagIndex === 0 ? 0 : 1 }}
            >
              <FontAwesome
                name='angle-left'
                color={Colors.light.hereworksBlue500}
                size={24}
              />
            </TouchableOpacity>
            <Text>{`${tagIndex + 1} / ${tags.length === 0 ? 1 : tags.length}`}</Text>
            <TouchableOpacity
              hitSlop={{
                top: 4, bottom: 4, left: 4, right: 4,
              }}
              onPress={() => setTagIndex(Math.min(tagIndex + 1, tags.length - 1))}
              disabled={tagIndex >= tags.length - 1}
              style={{ opacity: tagIndex === tags.length - 1 ? 0 : 1 }}
            >
              <FontAwesome
                name='angle-right'
                color={Colors.light.hereworksBlue500}
                size={24}
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
      <Text style={styles.linkTitle}>{'Generated QR link'}</Text>
      <View style={[styles.linkContainer, Style.row]}>
        <Text
          ellipsizeMode='tail'
          style={styles.link}
          numberOfLines={1}
        >
          {link}
        </Text>
        <TouchableOpacity onPress={copyLink}>
          <CopyIcon />
        </TouchableOpacity>
      </View>
      {copied && <Text style={[styles.linkTitle, styles.copiedText]}>{LINK_COPIED}</Text>}
      <View style={styles.addButtonContainer}>
        <PressableStyled
          style={styles.addButton}
          onPress={() => setAddQRCodeModalShown(true)}
          disabled={tags.length > 4}
        >
          <PlusIcon />
          <Text style={styles.buttonText}>Add another tag</Text>
        </PressableStyled>
        {tags.length > 4 && <Text style={[styles.linkTitle, { flex: 1 }]}>
          {'Maximum of 5 Solve tags per asset'}
        </Text>}
      </View>
    </View>
    <AddQRCodeModal
      {...{
        assetId, addQRCodeModalShown, hideQRCodeModal, tags,
      }}
      addTag={addTagToAsset}
    />
    <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    borderRadius: 10,
    backgroundColor: '#FFF',
  },
  tabContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  tabButton: {
    padding: 12,
    marginEnd: 4,
  },
  tabButtonText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
  tabButtonTextSelected: {
    color: Colors.light.hereworksBlue500,
  },
  content: {
    padding: 12,
  },
  data: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    borderRadius: 10,
    marginBottom: 16,
  },
  image: {
    padding: 12,
    width: 80,
    height: 80,
    justifyContent: 'center',
  },
  tagId: {
    justifyContent: 'space-between',
    padding: 10,
    flex: 1,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderStartColor: Colors.light.hereworksBlue200,
  },
  assetId: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginEnd: 16,
    flex: 1,
  },
  linkTitle: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 8,
  },
  linkContainer: {
    borderWidth: 0.5,
    borderColor: Colors.light.hereworksBlue200,
    borderRadius: 5,
    padding: 8,
  },
  link: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 0.9,
  },
  copiedText: {
    marginTop: 2,
    color: Colors.light.hereworksGreen500,
    fontFamily: 'Roboto_500Medium',
  },
  addButtonContainer: {
    marginTop: 16,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  addButton: {
    height: 40,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
    marginStart: undefined,
    marginEnd: 10,
    backgroundColor: Colors.light.hereworksBlue500,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: '#fff',
    marginLeft: 8,
  },
});

export default AssetTagSection;
